/**
 * HR Assessment Manager — create/edit test templates, send invitations,
 * review candidate sessions. Mounted at /company/assessments-v2.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatError } from "../api";
import {
  Plus, Trash2, Send, ClipboardCheck, Sparkles, X, Loader2, ShieldCheck,
  Mail, ChevronRight, ClipboardCopy, AlertTriangle, Pencil, Library, Filter,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

const QTYPES = [
  { v: "mcq", label: "MCQ" },
  { v: "boolean", label: "True/False" },
  { v: "coding", label: "Coding" },
];

function Btn({ children, ...rest }) {
  return <button {...rest} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${rest.className || ""}`}>{children}</button>;
}

function Card({ children, ...rest }) {
  return <div className="card card-pad" {...rest}>{children}</div>;
}

export default function CompanyAssessmentsV2() {
  usePageTitle("Test Assessments");
  const [tab, setTab] = useState("templates"); // templates | invitations | sessions
  return (
    <div className="space-y-6" data-testid="co-assessments-v2">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow">Assessments</span>
          <h2 className="font-display font-bold text-3xl mt-1">Test Engine</h2>
          <p className="text-muted text-sm mt-1 max-w-xl">Build skill-tests, invite candidates, review attempts with integrity signals.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-rule">
        {[
          { k: "templates",   label: "Templates",   Icon: ClipboardCheck },
          { k: "invitations", label: "Invitations", Icon: Mail },
          { k: "sessions",    label: "Sessions",    Icon: ShieldCheck },
        ].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 transition-colors ${
              tab === k ? "border-signal text-signal" : "border-transparent text-muted hover:text-fg"
            }`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {tab === "templates"   && <TemplatesTab />}
      {tab === "qbank"       && <QBankTab />}
      {tab === "invitations" && <InvitationsTab />}
      {tab === "sessions"    && <SessionsTab />}
    </div>
  );
}

// ─── Question Bank ──────────────────────────────────────────────────
function QBankTab() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ coe_lab: "", difficulty: "", type: "", search: "" });
  const [picked, setPicked] = useState({}); // qid → bool
  const [busy, setBusy] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [err, setErr] = useState("");

  const loadStats = useCallback(async () => {
    try { setStats((await api.get("/qbank/stats")).data); }
    catch (e) { setErr(formatError(e)); }
  }, []);

  const loadItems = useCallback(async () => {
    setErr("");
    try {
      const params = { limit: 100, offset: 0 };
      if (filters.coe_lab)    params.coe_lab    = filters.coe_lab;
      if (filters.difficulty) params.difficulty = filters.difficulty;
      if (filters.type)       params.type       = filters.type;
      if (filters.search)     params.search     = filters.search;
      const r = await api.get("/qbank/questions", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { setErr(formatError(e)); }
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const pickedIds = useMemo(() => Object.keys(picked).filter((k) => picked[k]), [picked]);
  const togglePick = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));

  const labLabel = stats?.labs || {};

  return (
    <div data-testid="qbank-tab" className="space-y-4">
      {/* Stats strip */}
      {stats && (
        <div className="card card-pad" data-testid="qbank-stats">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="eyebrow inline-flex items-center gap-1.5"><Library size={12} /> {stats.total} questions in the bank</div>
            <div className="text-[11px] text-muted">
              Easy: <b>{stats.by_difficulty?.easy || 0}</b> · Medium: <b>{stats.by_difficulty?.medium || 0}</b> · Hard: <b>{stats.by_difficulty?.hard || 0}</b>
              {" · "}MCQ: <b>{stats.by_type?.mcq || 0}</b> · Coding: <b>{stats.by_type?.coding || 0}</b>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card card-pad">
        <div className="flex items-center gap-2 mb-3"><Filter size={14} className="text-signal" /><span className="eyebrow">Filter</span></div>
        <div className="grid md:grid-cols-5 gap-3">
          <select className="input" value={filters.coe_lab} onChange={(e) => setFilters((f) => ({ ...f, coe_lab: e.target.value }))} data-testid="qbank-filter-lab">
            <option value="">All CoE labs</option>
            {Object.entries(labLabel).map(([k, label]) => (
              <option key={k} value={k}>{label}{stats?.by_lab?.[k] ? ` · ${stats.by_lab[k]}` : ""}</option>
            ))}
          </select>
          <select className="input" value={filters.difficulty} onChange={(e) => setFilters((f) => ({ ...f, difficulty: e.target.value }))} data-testid="qbank-filter-diff">
            <option value="">All difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
          <select className="input" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} data-testid="qbank-filter-type">
            <option value="">All types</option>
            <option value="mcq">MCQ</option>
            <option value="boolean">True/False</option>
            <option value="coding">Coding</option>
          </select>
          <input className="input md:col-span-2" placeholder="Search question text…" value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} data-testid="qbank-search" />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">{total} matched · {pickedIds.length} selected</div>
        <button className="btn-primary disabled:opacity-50" disabled={!pickedIds.length} onClick={() => setShowImport(true)} data-testid="qbank-import-btn">
          <Plus size={12} /> Import {pickedIds.length} into a template
        </button>
      </div>

      {err && <div className="card card-pad text-red-600 text-sm" data-testid="qbank-err">{err}</div>}

      {/* Questions */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="card card-pad text-center text-muted text-sm">No questions match. Try widening the filters.</div>
        ) : items.map((q) => (
          <div key={q.id} className={`card card-pad ${picked[q.id] ? "ring-2 ring-signal" : ""}`} data-testid={`qb-q-${q.id}`}>
            <div className="flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={!!picked[q.id]} onChange={() => togglePick(q.id)} data-testid={`qb-pick-${q.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: q.type === "coding" ? "#9333EA" : q.type === "boolean" ? "#0EA5E9" : "#16A34A" }}>{q.type}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "rgba(0,108,255,0.08)", color: "var(--signal)" }}>{labLabel[q.coe_lab] || q.coe_lab}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: q.difficulty === "hard" ? "#FECACA" : q.difficulty === "medium" ? "#FEF3C7" : "#DCFCE7", color: q.difficulty === "hard" ? "#B91C1C" : q.difficulty === "medium" ? "#A16207" : "#15803D" }}>{q.difficulty}</span>
                  {q.topic && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-paper border border-rule text-muted">{q.topic}</span>}
                  {q.source === "seed" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-paper border border-rule text-muted">seed</span>}
                  {q.source === "llm" && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-paper border border-rule text-muted">ai-generated</span>}
                </div>
                <div className="text-sm font-medium">{q.text}</div>
                {q.type === "mcq" && (
                  <ul className="text-xs text-muted mt-2 list-disc pl-5">
                    {(q.options || []).map((o, oi) => (
                      <li key={`${q.id}-${oi}-${(o || "").slice(0, 20)}`} className={oi === q.correct_index ? "text-emerald-700 font-semibold" : ""}>{o}{oi === q.correct_index ? " · correct" : ""}</li>
                    ))}
                  </ul>
                )}
                {q.type === "boolean" && <div className="text-xs text-muted mt-1">Correct: <b>{q.correct_boolean ? "True" : "False"}</b></div>}
                {q.explanation && <div className="text-[11px] text-muted mt-2 italic">💡 {q.explanation}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showImport && <ImportToTemplateModal questionIds={pickedIds} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); setPicked({}); }} />}
    </div>
  );
}

function ImportToTemplateModal({ questionIds, onClose, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/assessments/v2/templates")
      .then((r) => { setTemplates(r.data || []); if ((r.data || [])[0]) setTid(r.data[0].id); })
      .catch((e) => setErr(formatError(e)));
  }, []);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/qbank/import-to-template", {
        template_id: tid, question_ids: questionIds,
      });
      setDone(r.data);
    } catch (e) { setErr(formatError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="qbank-import-modal">
      <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-md hover:bg-paper grid place-items-center"><X size={18} /></button>
        <div className="eyebrow">Import {questionIds.length} questions</div>
        <h3 className="font-display font-bold text-xl mt-1">Pick a template</h3>
        {!done ? (
          <div className="mt-4 space-y-3">
            <select className="input" value={tid} onChange={(e) => setTid(e.target.value)} data-testid="qbank-import-template">
              {templates.length === 0 ? <option value="">Create a template first</option> : templates.map((t) => (
                <option key={t.id} value={t.id}>{t.title} · {t.question_count || 0} Qs</option>
              ))}
            </select>
            {err && <div className="text-red-600 text-sm">{err}</div>}
            <button className="btn-primary w-full justify-center disabled:opacity-50" disabled={busy || !tid} onClick={submit} data-testid="qbank-import-confirm">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              {busy ? "Importing…" : "Import"}
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl p-3 border border-emerald-300 bg-emerald-50 text-emerald-900">
              <div className="font-bold text-sm">✓ Imported {done.imported} question{done.imported === 1 ? "" : "s"}</div>
            </div>
            <button className="btn-primary w-full justify-center" onClick={onDone}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Templates ──────────────────────────────────────────────────────
function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // template id
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get("/assessments/v2/templates");
      setRows(r.data || []);
    } catch (e) { setErr(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createNew = async () => {
    try {
      const r = await api.post("/assessments/v2/templates", {
        title: "New Assessment", total_duration_min: 30, passing_score: 60,
      });
      await load();
      setEditing(r.data.id);
    } catch (e) { setErr(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this template (cascades all questions)?")) return;
    try { await api.delete(`/assessments/v2/templates/${id}`); await load(); }
    catch (e) { setErr(formatError(e)); }
  };

  if (editing) return <TemplateEditor id={editing} onBack={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="templates-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted">{rows.length} template{rows.length === 1 ? "" : "s"}</div>
        <Btn className="bg-signal text-white hover:opacity-90" onClick={createNew} data-testid="new-template-btn">
          <Plus size={12} /> New template
        </Btn>
      </div>
      {err && <div className="card card-pad text-red-600 text-sm">{err}</div>}
      {rows.length === 0 ? (
        <div className="card card-pad text-center text-muted text-sm">
          No templates yet — click <b>New template</b> to create your first one.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((t) => (
            <Card key={t.id}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-lg leading-tight" data-testid={`tpl-${t.id}-title`}>{t.title}</div>
                  <div className="text-[11px] text-muted mt-1">
                    {t.question_count || 0} Qs · {t.total_duration_min} min · ≥{t.passing_score}% pass · {t.invites_count || 0} invited
                  </div>
                  {t.description && <p className="text-xs text-muted mt-2 line-clamp-3">{t.description}</p>}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Btn className="bg-paper border border-rule hover:border-signal" onClick={() => setEditing(t.id)} data-testid={`tpl-edit-${t.id}`}>
                  <Pencil size={11} /> Edit
                </Btn>
                <Btn className="text-red-600 hover:bg-red-50 ml-auto" onClick={() => del(t.id)} data-testid={`tpl-del-${t.id}`}>
                  <Trash2 size={11} />
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ id, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/assessments/v2/templates/${id}`);
      setData(r.data);
    } catch (e) { setErr(formatError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="card card-pad text-muted">Loading…</div>;
  const t = data.template;

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.patch(`/assessments/v2/templates/${id}`, {
        title: t.title, description: t.description, total_duration_min: Number(t.total_duration_min),
        passing_score: Number(t.passing_score), sections: t.sections, randomize_questions: t.randomize_questions, allow_review: t.allow_review,
      });
    } catch (e) { setErr(formatError(e)); }
    finally { setSaving(false); }
  };

  const addQ = async (q) => {
    setAdding(true); setErr("");
    try {
      await api.post(`/assessments/v2/templates/${id}/questions`, q);
      await load();
    } catch (e) { setErr(formatError(e)); }
    finally { setAdding(false); }
  };

  const delQ = async (qid) => {
    try { await api.delete(`/assessments/v2/questions/${qid}`); await load(); }
    catch (e) { setErr(formatError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="template-editor">
      <button className="text-xs font-bold inline-flex items-center gap-1 text-signal" onClick={onBack} data-testid="editor-back">
        <ChevronRight size={12} className="rotate-180" /> Back to templates
      </button>

      <Card>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="eyebrow">Title</label>
            <input className="input mt-1.5" value={t.title} onChange={(e) => setData({ ...data, template: { ...t, title: e.target.value } })} data-testid="tpl-title" />
          </div>
          <div className="md:col-span-2">
            <label className="eyebrow">Description</label>
            <textarea className="input mt-1.5" value={t.description || ""} onChange={(e) => setData({ ...data, template: { ...t, description: e.target.value } })} data-testid="tpl-desc" />
          </div>
          <div>
            <label className="eyebrow">Total duration (min)</label>
            <input className="input mt-1.5" type="number" min="5" max="480" value={t.total_duration_min} onChange={(e) => setData({ ...data, template: { ...t, total_duration_min: e.target.value } })} data-testid="tpl-duration" />
          </div>
          <div>
            <label className="eyebrow">Passing score (%)</label>
            <input className="input mt-1.5" type="number" min="0" max="100" value={t.passing_score} onChange={(e) => setData({ ...data, template: { ...t, passing_score: e.target.value } })} data-testid="tpl-pass" />
          </div>
        </div>
        {err && <div className="text-red-600 text-sm mt-3">{err}</div>}
        <div className="mt-4 flex gap-2">
          <Btn className="bg-signal text-white" onClick={save} disabled={saving} data-testid="tpl-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <ClipboardCheck size={12} />}
            {saving ? "Saving…" : "Save"}
          </Btn>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-lg">Questions ({data.questions?.length || 0})</div>
        </div>
        <NewQuestionForm onAdd={addQ} adding={adding} />
        <div className="mt-4 space-y-2">
          {(data.questions || []).map((q, i) => (
            <div key={q.id} className="p-3 rounded-lg border border-rule" data-testid={`q-row-${q.id}`}>
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: q.type === "coding" ? "#9333EA" : q.type === "boolean" ? "#0EA5E9" : "#16A34A" }}>{q.type}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{i + 1}. {q.text}</div>
                  {q.type === "mcq" && (
                    <ul className="text-xs text-muted mt-1 list-disc pl-5">
                      {(q.options || []).map((o, oi) => (
                        <li key={`${q.id}-opt-${oi}-${o.slice(0, 32)}`} className={oi === q.correct_index ? "text-emerald-700 font-semibold" : ""}>{o}{oi === q.correct_index ? " · correct" : ""}</li>
                      ))}
                    </ul>
                  )}
                  {q.type === "boolean" && <div className="text-xs text-muted mt-1">Correct: <b>{q.correct_boolean ? "True" : "False"}</b></div>}
                </div>
                <button onClick={() => delQ(q.id)} className="text-red-600 hover:bg-red-50 rounded p-1" data-testid={`q-del-${q.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
          {(data.questions || []).length === 0 && <div className="text-center text-muted text-sm py-4">Add your first question above.</div>}
        </div>
      </Card>
    </div>
  );
}

function NewQuestionForm({ onAdd, adding }) {
  const [type, setType] = useState("mcq");
  const [text, setText] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [correctIdx, setCorrectIdx] = useState(0);
  const [boolAns, setBoolAns] = useState(true);
  const [starter, setStarter] = useState("");
  const [points, setPoints] = useState(1);

  const reset = () => { setText(""); setOptions(["", ""]); setCorrectIdx(0); setBoolAns(true); setStarter(""); setPoints(1); };

  const submit = () => {
    if (!text.trim()) return;
    const base = { text: text.trim(), type, points: Number(points) || 1 };
    if (type === "mcq") {
      const opts = options.map((o) => o.trim()).filter(Boolean);
      if (opts.length < 2) return;
      onAdd({ ...base, options: opts, correct_index: Math.min(correctIdx, opts.length - 1) });
    } else if (type === "boolean") {
      onAdd({ ...base, correct_boolean: boolAns });
    } else {
      onAdd({ ...base, starter_code: starter, language: "javascript" });
    }
    reset();
  };

  return (
    <div className="rounded-lg border border-rule p-3 bg-paper space-y-3" data-testid="new-question-form">
      <div className="flex gap-2 flex-wrap">
        {QTYPES.map((qt) => (
          <button key={qt.v} type="button" onClick={() => setType(qt.v)} data-testid={`qtype-${qt.v}`}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border transition-all ${
              type === qt.v ? "bg-signal text-white border-signal" : "bg-white border-rule hover:border-signal"
            }`}>{qt.label}</button>
        ))}
      </div>
      <textarea className="input min-h-[60px]" value={text} onChange={(e) => setText(e.target.value)} placeholder="Question text…" data-testid="newq-text" />

      {type === "mcq" && (
        <div className="space-y-1.5">
          {options.map((opt, i) => (
            <div key={`newopt-${i}-${opt.slice(0, 24)}`} className="flex items-center gap-2">
              <input type="radio" checked={correctIdx === i} onChange={() => setCorrectIdx(i)} data-testid={`newq-correct-${i}`} />
              <input className="input flex-1" value={opt} onChange={(e) => setOptions((arr) => arr.map((o, j) => j === i ? e.target.value : o))} placeholder={`Option ${i + 1}`} data-testid={`newq-opt-${i}`} />
              {options.length > 2 && <button type="button" onClick={() => setOptions((arr) => arr.filter((_, j) => j !== i))} className="text-red-600"><Trash2 size={12} /></button>}
            </div>
          ))}
          <Btn type="button" className="border border-rule" onClick={() => setOptions((arr) => [...arr, ""])} data-testid="newq-add-opt"><Plus size={11} /> Add option</Btn>
        </div>
      )}

      {type === "boolean" && (
        <div className="flex gap-2">
          {[true, false].map((v) => (
            <button key={String(v)} type="button" onClick={() => setBoolAns(v)}
              className={`text-xs font-bold px-4 py-1.5 rounded-md border ${boolAns === v ? "bg-signal text-white border-signal" : "border-rule"}`}
              data-testid={`newq-bool-${v}`}>{v ? "True" : "False"}</button>
          ))}
        </div>
      )}

      {type === "coding" && (
        <textarea className="input min-h-[90px] font-mono text-xs" value={starter} onChange={(e) => setStarter(e.target.value)} placeholder="Starter code (optional)…" data-testid="newq-starter" />
      )}

      <div className="flex items-center gap-2">
        <label className="text-xs text-muted">Points</label>
        <input className="input w-20" type="number" min="0.5" step="0.5" value={points} onChange={(e) => setPoints(e.target.value)} data-testid="newq-points" />
        <Btn type="button" className="bg-signal text-white ml-auto" onClick={submit} disabled={adding} data-testid="newq-add">
          {adding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add question
        </Btn>
      </div>
    </div>
  );
}

// ─── Invitations ───────────────────────────────────────────────────
function InvitationsTab() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [a, b] = await Promise.all([
        api.get("/assessments/v2/invitations"),
        api.get("/assessments/v2/templates"),
      ]);
      setRows(a.data || []);
      setTemplates(b.data || []);
    } catch (e) { setErr(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="invitations-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted">{rows.length} invitation{rows.length === 1 ? "" : "s"}</div>
        <Btn className="bg-signal text-white" onClick={() => setShowForm(true)} data-testid="invite-btn">
          <Send size={12} /> Invite candidate
        </Btn>
      </div>
      {err && <div className="card card-pad text-red-600 text-sm">{err}</div>}

      {showForm && <InviteModal templates={templates} onClose={() => setShowForm(false)} onCreated={load} />}

      {rows.length === 0 ? (
        <div className="card card-pad text-center text-muted text-sm">No invitations yet.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="dense w-full" data-testid="invitations-table">
            <thead><tr><th>Candidate</th><th>Template</th><th>Status</th><th>Score</th><th>Integrity</th><th>Sent</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`inv-row-${r.id}`}>
                  <td>
                    <div className="font-medium">{r.candidate_name || "—"}</div>
                    <div className="text-[11px] text-muted">{r.candidate_email}</div>
                  </td>
                  <td>{r.template_title}</td>
                  <td>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white" style={{
                      background: r.status === "completed" ? "#16A34A" :
                        r.status === "started" ? "#0EA5E9" :
                        r.status === "expired" ? "#94A3B8" : "#9333EA"
                    }}>{r.status}</span>
                  </td>
                  <td className="font-mono tabular-nums">{r.session_summary?.score_pct != null ? `${r.session_summary.score_pct}%` : "—"}</td>
                  <td className="font-mono tabular-nums">{r.session_summary?.integrity_score != null ? `${r.session_summary.integrity_score}/100` : "—"}</td>
                  <td className="text-[11px]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                  <td>
                    <button onClick={() => navigator.clipboard?.writeText(r.invitation_link || "")}
                      className="text-signal hover:underline inline-flex items-center gap-1 text-xs font-bold" data-testid={`inv-copy-${r.id}`}>
                      <ClipboardCopy size={11} /> Copy link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InviteModal({ templates, onClose, onCreated }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // {invitation_link}

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!templateId || !email) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/assessments/v2/invite", {
        template_id: templateId, candidate_email: email, candidate_name: name || null, expires_in_days: Number(days),
      });
      setDone(r.data);
      onCreated?.();
    } catch (e2) { setErr(formatError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-modal">
      <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <form onSubmit={submit} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-md hover:bg-paper grid place-items-center"><X size={18} /></button>
        <div className="eyebrow">Send invite</div>
        <h3 className="font-display font-bold text-xl mt-1">Invite a candidate</h3>

        {!done ? (
          <div className="mt-4 space-y-3">
            <div>
              <label className="eyebrow">Template</label>
              <select className="input mt-1.5" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="invite-template" required>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title} · {t.question_count || 0} Qs</option>)}
              </select>
            </div>
            <div>
              <label className="eyebrow">Candidate email</label>
              <input className="input mt-1.5" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="invite-email" />
            </div>
            <div>
              <label className="eyebrow">Candidate name (optional)</label>
              <input className="input mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" />
            </div>
            <div>
              <label className="eyebrow">Expires in (days)</label>
              <input className="input mt-1.5" type="number" min="1" max="60" value={days} onChange={(e) => setDays(e.target.value)} data-testid="invite-days" />
            </div>
            {err && <div className="text-red-600 text-sm">{err}</div>}
            <Btn type="submit" className="bg-signal text-white w-full justify-center" disabled={busy} data-testid="invite-submit">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {busy ? "Sending…" : "Generate invite link"}
            </Btn>
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="invite-success">
            <div className="rounded-xl p-3 border border-emerald-300 bg-emerald-50 text-emerald-900">
              <div className="font-bold text-sm">Invite link generated!</div>
              <div className="text-xs mt-1">Share this link with the candidate. It expires in {days} days.</div>
            </div>
            <div className="font-mono text-xs break-all p-3 rounded-lg bg-paper border border-rule">{done.invitation_link}</div>
            <Btn className="bg-signal text-white w-full justify-center"
              onClick={() => navigator.clipboard?.writeText(done.invitation_link)} data-testid="invite-copy-success">
              <ClipboardCopy size={12} /> Copy link
            </Btn>
            <Btn className="border border-rule w-full justify-center" onClick={onClose}>Done</Btn>
          </div>
        )}
      </form>
    </div>
  );
}

// ─── Sessions ───────────────────────────────────────────────────────
function SessionsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null); // {session, questions}
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/assessments/v2/invitations?status=completed");
      setRows(r.data || []);
    } catch (e) { setErr(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open_ = async (sid) => {
    try {
      const r = await api.get(`/assessments/v2/sessions/${sid}`);
      setOpen(r.data);
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <div data-testid="sessions-tab">
      {err && <div className="card card-pad text-red-600 text-sm mb-4">{err}</div>}
      {rows.length === 0 ? (
        <div className="card card-pad text-center text-muted text-sm">No completed sessions yet — once a candidate submits, results will land here.</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="dense w-full">
            <thead><tr><th>Candidate</th><th>Template</th><th>Score</th><th>Integrity</th><th>Submitted</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`sess-${r.id}`}>
                  <td>
                    <div className="font-medium">{r.candidate_name || "—"}</div>
                    <div className="text-[11px] text-muted">{r.candidate_email}</div>
                  </td>
                  <td>{r.template_title}</td>
                  <td className="font-mono font-bold tabular-nums" style={{ color: (r.session_summary?.score_pct || 0) >= 60 ? "#16A34A" : "#DC2626" }}>
                    {r.session_summary?.score_pct ?? "—"}{r.session_summary?.score_pct != null ? "%" : ""}
                  </td>
                  <td className="font-mono tabular-nums">
                    <IntegrityBadge score={r.session_summary?.integrity_score} />
                  </td>
                  <td className="text-[11px]">{r.session_summary?.submitted_at ? new Date(r.session_summary.submitted_at).toLocaleString() : ""}</td>
                  <td>
                    <Btn className="text-signal hover:underline" onClick={() => open_(r.session_summary?.id)}>View details</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <SessionDetailModal data={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function IntegrityBadge({ score }) {
  if (score == null) return <span className="text-muted">—</span>;
  const tone = score >= 80 ? { bg: "#DCFCE7", fg: "#15803D" } : score >= 50 ? { bg: "#FEF3C7", fg: "#A16207" } : { bg: "#FEE2E2", fg: "#B91C1C" };
  return <span className="text-[11px] font-bold px-2 py-0.5 rounded" style={{ background: tone.bg, color: tone.fg }}>{score}/100</span>;
}

function SessionDetailModal({ data, onClose }) {
  const s = data.session;
  const qmap = useMemo(() => Object.fromEntries((data.questions || []).map((q) => [q.id, q])), [data.questions]);
  const [snapshots, setSnapshots] = useState([]);
  useEffect(() => {
    api.get(`/assessments/v2/sessions/${s.id}/snapshots`)
      .then((r) => setSnapshots(r.data || []))
      .catch(() => setSnapshots([]));
  }, [s.id]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button onClick={onClose} aria-label="Close" className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto" data-testid="session-modal">
        <header className="sticky top-0 z-10 bg-white border-b p-5 flex items-start gap-3" style={{ borderColor: "var(--rule)" }}>
          <div className="min-w-0">
            <div className="eyebrow">Session</div>
            <h3 className="font-display font-bold text-xl mt-1">{s.candidate_name || s.candidate_email}</h3>
            <div className="text-xs text-muted mt-1">
              Score: <b style={{ color: (s.score_pct || 0) >= 60 ? "#16A34A" : "#DC2626" }}>{s.score_pct}%</b>
              {" · "}Integrity: <IntegrityBadge score={s.integrity_score} />
              {" · "}Duration: {s.duration_sec ? `${Math.floor(s.duration_sec / 60)}m ${s.duration_sec % 60}s` : "—"}
            </div>
          </div>
          <button onClick={onClose} className="ml-auto w-9 h-9 rounded-md hover:bg-paper grid place-items-center"><X size={18} /></button>
        </header>

        <div className="p-5 space-y-4">
          {/* Integrity events */}
          {s.integrity_events && (
            <div className="card card-pad">
              <div className="eyebrow inline-flex items-center gap-1"><AlertTriangle size={11} /> Integrity events</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-[11px]">
                {Object.entries(s.integrity_events).map(([k, v]) => (
                  <div key={k} className="rounded bg-paper p-2 border border-rule">
                    <div className="font-mono font-bold text-base">{v}</div>
                    <div className="text-muted">{k.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Webcam snapshots */}
          {snapshots.length > 0 && (
            <div className="card card-pad" data-testid="snapshots-grid">
              <div className="eyebrow inline-flex items-center gap-1">📷 Webcam snapshots · {snapshots.length}</div>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
                {snapshots.map((sn, i) => (
                  <a key={sn.id || i} href={sn.url} target="_blank" rel="noopener noreferrer" className="block rounded-md overflow-hidden border border-rule hover:border-signal transition-colors" data-testid={`snap-${i}`} title={sn.taken_at}>
                    <img src={sn.url} alt={`snap ${i + 1}`} className="w-full h-20 object-cover" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Question breakdown */}
          <div className="space-y-2">
            <div className="eyebrow">Answers</div>
            {(s.breakdown || []).map((b, i) => {
              const q = qmap[b.question_id];
              const a = (s.answers || []).find((x) => x.question_id === b.question_id);
              return (
                <div key={b.question_id} className="rounded-lg border border-rule p-3" data-testid={`sess-q-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: b.correct ? "#16A34A" : b.correct === null ? "#9333EA" : "#DC2626" }}>
                      {b.correct ? "correct" : b.correct === null ? "pending" : "wrong"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{i + 1}. {q?.text || "(deleted question)"}</div>
                      {q?.type === "mcq" && a?.selected_index != null && (
                        <div className="text-xs mt-1">
                          Answered: <b>{q.options?.[a.selected_index]}</b>
                          {q.correct_index !== a.selected_index && q.options?.[q.correct_index] && (
                            <span className="text-emerald-700 ml-2">Correct: {q.options[q.correct_index]}</span>
                          )}
                        </div>
                      )}
                      {q?.type === "boolean" && a?.boolean_answer != null && (
                        <div className="text-xs mt-1">Answered: <b>{a.boolean_answer ? "True" : "False"}</b></div>
                      )}
                      {q?.type === "coding" && (
                        <pre className="text-[11px] font-mono mt-2 bg-paper p-2 rounded max-h-32 overflow-auto">{a?.code_answer || "(no code submitted)"}</pre>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
